import {
  Badge,
  Card,
  CardContent,
  cn,
  EmptyState,
  formatYearMonth,
  Spinner,
  Tabs,
  TabsList,
  TabsTab,
} from "@forge/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { CircleCheck, Rocket } from "lucide-react";
import type { ReactNode } from "react";
import { PageHeader } from "../../components/page-header";
import {
  ProfitQualityBadge,
  ProfitReadiness,
} from "../../components/profit-readiness";
import { ReputationCard } from "../../components/reputation-card";
import { api, unwrap } from "../../lib/api";
import { canAccessArea } from "../../lib/areas";
import { meQueryOptions } from "../../lib/auth";
import { formatBRL } from "../../lib/format";

type HomeRange = "7d" | "30d" | "90d";

export const Route = createFileRoute("/_app/home")({
  validateSearch: (search: Record<string, unknown>): { range?: HomeRange } => ({
    range:
      search.range === "7d" || search.range === "30d" || search.range === "90d"
        ? search.range
        : undefined,
  }),
  component: DashboardPage,
});

const KIND_LABEL = {
  claim: "Reclamação",
  order: "Pedido",
  question: "Pergunta",
  message: "Mensagem",
  label: "Etiqueta",
  replenish: "Reposição",
  listing: "Anúncio",
} as const;

type CockpitKindKey = keyof typeof KIND_LABEL;

const URGENCY_VARIANT: Record<string, "danger" | "warning" | "neutral"> = {
  now: "danger",
  today: "warning",
  soon: "neutral",
};

function DashboardPage() {
  const me = useQuery(meQueryOptions);
  const showFinance = canAccessArea({
    role: me.data && "role" in me.data ? me.data.role : null,
    areas: me.data && "areas" in me.data ? me.data.areas : null,
    area: "finance",
  });
  const search = Route.useSearch();
  const range: HomeRange = search.range ?? "30d";
  const navigate = Route.useNavigate();
  const queryClient = useQueryClient();
  const notices = useQuery({
    queryKey: ["notices"],
    queryFn: async () => unwrap(await api.notices.get()),
  });
  const dismissNotice = useMutation({
    mutationFn: async (id: string) =>
      unwrap(await api.notices({ id }).dismiss.post()),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["notices"] });
    },
  });
  const cockpit = useQuery({
    queryKey: ["cockpit", range],
    queryFn: async () =>
      unwrap(await api.cockpit.get({ query: { range } })),
  });
  const setup = useQuery({
    queryKey: ["setup"],
    queryFn: async () => unwrap(await api.setup.get()),
  });

  if (cockpit.isLoading) {
    return (
      <div className="flex justify-center py-20">
        <Spinner className="size-5 text-ink-muted" />
      </div>
    );
  }

  const data = cockpit.data;
  const pending = data
    ? data.counts.claim +
      data.counts.order +
      data.counts.question +
      data.counts.message +
      data.counts.label +
      data.counts.listing +
      data.counts.replenish
    : 0;

  return (
    <>
      <PageHeader
        title="Hoje"
        description={
          data
            ? pending > 0
              ? `${pending} pendência${pending === 1 ? "" : "s"} · ${formatYearMonth(data.month.key)}`
              : `Nada pendente · ${formatYearMonth(data.month.key)}`
            : "O que precisa da sua atenção agora"
        }
      />

      {setup.data && setup.data.step !== "done" && (
        <Card className="mb-5 border-ember/25 bg-ember-subtle">
          <CardContent className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-start gap-2.5">
              <Rocket className="mt-0.5 size-4 shrink-0 text-ember-strong" />
              <div>
                <p className="text-[0.8125rem] font-semibold text-ink">
                  {setup.data.step === "connect"
                    ? "Conecte sua primeira loja"
                    : setup.data.step === "import"
                      ? "Importe anúncios e pedidos"
                      : "Vincule os anúncios a um SKU"}
                </p>
                <p className="mt-0.5 text-2xs text-ink-muted">
                  Três passos para o estoque central acompanhar as vendas.
                </p>
              </div>
            </div>
            <Link
              to="/setup"
              className="text-2xs font-medium text-ember-strong hover:underline"
            >
              Continuar configuração →
            </Link>
          </CardContent>
        </Card>
      )}

      {notices.data && notices.data.items.length > 0 ? (
        <Card className="mb-5">
          <CardContent className="flex flex-col gap-3 p-4">
            <p className="text-2xs font-medium uppercase tracking-wider text-ink-muted">
              Comunicados do Mercado Livre
            </p>
            <ul className="flex flex-col gap-3">
              {notices.data.items.map((notice) => (
                <li
                  key={notice.id}
                  className="flex flex-col gap-2 border-b border-line pb-3 last:border-0 last:pb-0 sm:flex-row sm:items-start sm:justify-between"
                >
                  <div className="min-w-0">
                    <p className="text-[0.8125rem] font-medium text-ink">
                      {notice.label}
                    </p>
                    {notice.description ? (
                      <p className="mt-0.5 text-2xs text-ink-muted">
                        {notice.description}
                      </p>
                    ) : null}
                  </div>
                  <button
                    type="button"
                    className="shrink-0 text-2xs font-medium text-ink-muted transition-transform duration-100 ease-swift active:scale-[0.97] [@media(hover:hover)_and_(pointer:fine)]:hover:text-ink"
                    onClick={() => dismissNotice.mutate(notice.id)}
                  >
                    Dispensar
                  </button>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      ) : null}

      {showFinance &&
      setup.data?.step === "done" &&
      data?.month.readiness &&
      !data.month.readiness.ready ? (
        <ProfitReadiness readiness={data.month.readiness} className="mb-5" />
      ) : null}

      <div className="mb-2 flex justify-end">
        <Tabs
          value={range}
          onValueChange={(value) => {
            if (value === "7d" || value === "30d" || value === "90d") {
              void navigate({ to: "/home", search: { range: value } });
            }
          }}
        >
          <TabsList>
            <TabsTab value="7d">7d</TabsTab>
            <TabsTab value="30d">30d</TabsTab>
            <TabsTab value="90d">90d</TabsTab>
          </TabsList>
        </Tabs>
      </div>
      <div className="mb-5 grid grid-cols-1 gap-3 sm:grid-cols-3">
        {showFinance && (
        <Link to="/profit" className="rounded-control">
          <MetricCard
            label={`Lucro · ${data ? formatYearMonth(data.month.key) : "mês"}`}
            value={
              data?.month.quality === "empty"
                ? "—"
                : formatBRL(data?.month.profit ?? "0")
            }
            badge={
              <ProfitQualityBadge quality={data?.month.quality ?? "empty"} />
            }
            hint={
              data?.month.quality === "complete"
                ? "Conferido no extrato"
                : data?.month.quality === "partial"
                  ? "Parcial — falta taxa, custo ou extrato"
                  : "Sem pedidos pagos neste mês"
            }
          />
        </Link>
        )}
        <MetricCard
          label={`Receita (${data?.last30Days.days ?? 30} dias)`}
          value={formatBRL(data?.last30Days.paidRevenue ?? "0")}
        />
        <MetricCard
          label={`Pedidos (${data?.last30Days.days ?? 30} dias)`}
          value={String(data?.last30Days.paidCount ?? 0)}
        />
      </div>

      {data?.reputation ? (
        <div className="mb-5">
          <ReputationCard report={data.reputation} />
        </div>
      ) : null}

      {data && (data.queue.length > 0 || (data.later?.length ?? 0) > 0) ? (
        <Card>
          <div className="flex flex-wrap gap-1 border-b border-line px-3 py-2">
            {(
              [
                "claim",
                "order",
                "question",
                "message",
                "label",
                "listing",
                "replenish",
              ] as const
            )
              .filter((kind) => data.counts[kind] > 0)
              .map((kind) => (
                <KindChip
                  key={kind}
                  kind={kind}
                  count={data.counts[kind]}
                />
              ))}
          </div>
          <ul>
            {data.queue.map((item, index) => (
              <li
                key={`${item.kind}-${item.id}`}
                className={
                  index < data.queue.length - 1 || (data.later?.length ?? 0) > 0
                    ? "border-b border-line"
                    : undefined
                }
              >
                <QueueRow item={item} />
              </li>
            ))}
            {(data.later ?? []).map((row, index) => (
              <li
                key={`later-${row.kind}`}
                className={
                  index < (data.later?.length ?? 0) - 1
                    ? "border-b border-line"
                    : undefined
                }
              >
                <LaterRow row={row} />
              </li>
            ))}
          </ul>
        </Card>
      ) : (
        <Card>
          <EmptyState
            icon={<CircleCheck />}
            title="Nada pendente agora"
            description="Perguntas, reclamações, anúncios doentes, etiquetas e reposição aparecem aqui quando precisarem de você."
          />
        </Card>
      )}
    </>
  );
}

function KindChip({
  kind,
  count,
}: {
  kind: CockpitKindKey;
  count: number;
}) {
  const className = cn(
    "inline-flex h-7 items-center gap-1.5 rounded-full px-2.5 text-2xs font-medium text-ink-secondary",
    "transition-transform duration-100 ease-swift active:scale-[0.97]",
    "[@media(hover:hover)_and_(pointer:fine)]:hover:bg-surface-sunken",
  );
  const label = (
    <>
      {KIND_LABEL[kind]}
      <span className="font-mono text-ink-faint">{count}</span>
    </>
  );
  switch (kind) {
    case "claim":
      return (
        <Link to="/claims" className={className}>
          {label}
        </Link>
      );
    case "order":
      return (
        <Link to="/orders" search={{ page: 1 }} className={className}>
          {label}
        </Link>
      );
    case "question":
      return (
        <Link to="/questions" search={{ page: 1 }} className={className}>
          {label}
        </Link>
      );
    case "message":
      return (
        <Link to="/messages" className={className}>
          {label}
        </Link>
      );
    case "label":
      return (
        <Link to="/fulfillment" className={className}>
          {label}
        </Link>
      );
    case "listing":
      return (
        <Link to="/listings" className={className}>
          {label}
        </Link>
      );
    case "replenish":
      return (
        <Link to="/purchases" className={className}>
          {label}
        </Link>
      );
    default:
      return null;
  }
}

function LaterRow({
  row,
}: {
  row: {
    kind: string;
    count: number;
    label: string;
    href: { to: string };
  };
}) {
  const className =
    "flex min-w-0 w-full items-center justify-between gap-3 px-4 py-3 active:scale-[0.99] [@media(hover:hover)_and_(pointer:fine)]:hover:bg-surface-sunken";
  const body = (
    <>
      <div className="min-w-0">
        <Badge variant="neutral">
          {KIND_LABEL[row.kind as CockpitKindKey] ?? row.kind}
        </Badge>
        <p className="mt-1 truncate text-[0.8125rem] font-medium text-ink">
          {row.label}
        </p>
        <p className="mt-0.5 text-2xs text-ink-muted">Em breve · abre a lista</p>
      </div>
      <span className="shrink-0 text-2xs font-medium text-ember-strong">
        Ver →
      </span>
    </>
  );
  switch (row.href.to) {
    case "/listings":
      return (
        <Link to="/listings" className={className}>
          {body}
        </Link>
      );
    case "/purchases":
      return (
        <Link to="/purchases" className={className}>
          {body}
        </Link>
      );
    case "/claims":
      return (
        <Link to="/claims" className={className}>
          {body}
        </Link>
      );
    case "/questions":
      return (
        <Link to="/questions" search={{ page: 1 }} className={className}>
          {body}
        </Link>
      );
    case "/messages":
      return (
        <Link to="/messages" className={className}>
          {body}
        </Link>
      );
    case "/fulfillment":
      return (
        <Link to="/fulfillment" className={className}>
          {body}
        </Link>
      );
    case "/orders":
      return (
        <Link to="/orders" search={{ page: 1 }} className={className}>
          {body}
        </Link>
      );
    default:
      return <div className={className}>{body}</div>;
  }
}

function QueueRow({
  item,
}: {
  item: {
    kind: string;
    id: string;
    title: string;
    detail: string;
    urgency: string;
    href:
      | { to: "/orders/$id"; params: { id: string } }
      | { to: "/questions" }
      | { to: "/claims" }
      | { to: "/messages" }
      | { to: "/fulfillment" }
      | { to: "/stock" }
      | { to: "/purchases" }
      | { to: "/listings/$id"; params: { id: string } };
  };
}) {
  const className =
    "flex min-w-0 w-full items-center justify-between gap-3 px-4 py-3 active:scale-[0.99] [@media(hover:hover)_and_(pointer:fine)]:hover:bg-surface-sunken";
  const body = (
    <>
      <div className="min-w-0">
        <div className="flex items-center gap-1.5">
          <Badge variant={URGENCY_VARIANT[item.urgency] ?? "neutral"}>
            {KIND_LABEL[item.kind as CockpitKindKey] ?? item.kind}
          </Badge>
        </div>
        <p className="mt-1 truncate text-[0.8125rem] font-medium text-ink">
          {item.title}
        </p>
        <p className="mt-0.5 truncate text-2xs text-ink-muted">{item.detail}</p>
      </div>
      <span className="shrink-0 text-2xs font-medium text-ember-strong">
        Resolver →
      </span>
    </>
  );

  if (item.href.to === "/orders/$id" || item.href.to === "/listings/$id") {
    return (
      <Link to={item.href.to} params={item.href.params} className={className}>
        {body}
      </Link>
    );
  }
  return (
    <Link to={item.href.to} className={className}>
      {body}
    </Link>
  );
}

function MetricCard({
  label,
  value,
  hint,
  badge,
}: {
  label: string;
  value: string;
  hint?: string;
  badge?: ReactNode;
}) {
  return (
    <Card>
      <CardContent className="p-3.5" data-slot="metric">
        <div className="flex items-center justify-between gap-2">
          <p className="text-2xs font-medium uppercase tracking-wider text-ink-muted">
            {label}
          </p>
          {badge}
        </div>
        <p
          className={cn(
            "mt-1 font-mono text-xl font-medium tracking-tight text-ink",
          )}
        >
          {value}
        </p>
        {hint ? <p className="mt-1 text-2xs text-ink-muted">{hint}</p> : null}
      </CardContent>
    </Card>
  );
}
